/** Cart line health.
 *
 *  A cart lives in localStorage and can outlive the page that built it, so every
 *  line is re-validated on load and again before checkout. The outcomes are kept
 *  DISTINCT because the right thing to say to the customer differs completely:
 *
 *   SELF_CONFLICT  you already hold this slot on an unpaid order of your own
 *   EXPIRED        the departure time has passed
 *   UNPRICEABLE    the vendor can no longer price it (someone else took it)
 *   PRICE_CHANGED  still bookable, but not at the price you were shown
 *
 *  A $0.00 line with a live checkout button reached a real customer because all
 *  of these were collapsed into one vague "pricing or availability changed".
 */
import { findSelfConflict } from "./pendingOrders";

export const OK = "ok";
export const PRICE_CHANGED = "price_changed";
export const EXPIRED = "expired";
export const UNPRICEABLE = "unpriceable";
export const SELF_CONFLICT = "self_conflict";
export const PRICING_UNKNOWN = "pricing_unknown";

/** Blocks checkout and is excluded from the total. */
export const isBlocking = (state) =>
  state === EXPIRED || state === UNPRICEABLE || state === SELF_CONFLICT || state === PRICING_UNKNOWN;

/** A quote that failed for a TRANSIENT reason (rate limit, upstream wobble,
 *  dropped connection) says nothing about the boat. Accusing it of being "no
 *  longer available" would be a guess dressed up as a fact, so those get their
 *  own honest state. Only a definitive verdict condemns a line. */
const DEFINITIVE_CODES = new Set(["unpriceable_selection", "departure_in_past"]);

export const isDefinitiveQuoteFailure = (err) =>
  Boolean(err && DEFINITIVE_CODES.has(err.code));

export const STATE_COPY = {
  [EXPIRED]: {
    title: "This departure time has passed",
    detail: "Pick a new date or departure time, or remove this boat to carry on.",
  },
  [UNPRICEABLE]: {
    title: "No longer available",
    detail: "This boat has been taken for that date and time. Pick another time, or remove it to carry on.",
  },
  [SELF_CONFLICT]: {
    title: "You've already reserved this boat for this time",
    detail: "This is a leftover copy of a reservation you already hold. Remove it here and finish paying for your existing reservation.",
  },
  [PRICING_UNKNOWN]: {
    title: "We couldn't check this price just now",
    detail: "Our booking system didn't answer. Nothing is wrong with your booking — please try again in a moment.",
  },
  [PRICE_CHANGED]: {
    title: "The price for this boat has changed",
    detail: "Have a look at the new price below and confirm you're happy before booking.",
  },
};

/** Cart lines flatten into vendor lines (each add-on is its own line), so the
 *  quote's line N is NOT cart line N. Returns the vendor index for cart line i. */
export const vendorLineIndex = (lines, i) =>
  lines.slice(0, i).reduce((n, l) => n + 1 + (l.addons || []).length, 0);

/** Has this line's departure already gone? Judged with NO network call, so a
 *  genuinely old cart is caught before we ask the vendor anything. */
export const hasDeparted = (line, now = new Date()) => {
  if (!line?.date || !line?.startTime) return false;
  const departure = new Date(`${line.date}T${line.startTime}`);
  if (Number.isNaN(departure.getTime())) return false;
  return departure.getTime() < now.getTime() - 2 * 60 * 1000;
};

/**
 * @param line  cart line
 * @param ql    the matching vendor quote line, if we have one
 * @param opts  { quoteError, now, pending }
 */
export const classifyLine = (line, ql, opts = {}) => {
  const { quoteError = null, now = new Date(), pending } = opts;
  const priced = ql && (ql.totalAmount || 0) > 0;
  const definitive = isDefinitiveQuoteFailure(quoteError);

  // A departure that has gone is a fact, whoever holds the boat.
  if (hasDeparted(line, now)) return { state: EXPIRED, ...STATE_COPY[EXPIRED] };

  // Checked BEFORE any verdict about availability, and BEFORE blaming a wobble:
  // the pending-order record is a FACT about what this browser booked, not a
  // guess, and it explains an unpriceable line at zero cost. Telling customers a
  // boat is taken when THEY are the ones holding it is both wrong and
  // infuriating. A line that still prices fine is simply a repeat booking, which
  // is allowed, so this only fires when the line will not price.
  if (!priced && (quoteError || ql)) {
    const conflict = findSelfConflict(line, pending);
    if (conflict) {
      return { state: SELF_CONFLICT, ...STATE_COPY[SELF_CONFLICT], pendingOrder: conflict };
    }
  }

  if (quoteError) {
    return definitive
      ? { state: UNPRICEABLE, ...STATE_COPY[UNPRICEABLE] }
      : { state: PRICING_UNKNOWN, ...STATE_COPY[PRICING_UNKNOWN] };
  }
  if (!ql) return { state: OK };

  // A boat the vendor prices at nothing is the out-of-window $0 bug, not a gift.
  if (!priced) return { state: UNPRICEABLE, ...STATE_COPY[UNPRICEABLE] };

  const expected = (line.amount || 0) * (line.quantity || 1);
  if (expected > 0 && Math.abs(expected - (ql.baseAmount || 0)) > 0.005) {
    return {
      state: PRICE_CHANGED,
      ...STATE_COPY[PRICE_CHANGED],
      oldAmount: expected,
      newAmount: ql.baseAmount,
    };
  }
  return { state: OK };
};
